import { useEffect, useId, useState } from 'react';
import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  can,
  formatQty,
  ADJUSTMENT_REASON_CODES,
  ADJUSTMENT_REASON_LABELS,
  type AdjustmentReasonCode,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { createIntentKey } from '../../lib/idempotency';
import { useFailedWriteFor } from '../../lib/failed-writes';
import { fieldErrorOf, fieldErrorAt, numberTextIssue } from '../../lib/field-error';
import { useSession } from '../session';
import { useOnline, useUnsentWrite } from '../ui/useConnection';
import {
  mutationState,
  ErrorState,
  OfflineState,
  shownState,
  StaleNotice,
  refreshFailed,
} from '../ui/QueryStates';
import { IconInventory } from '../icons';
import { Sheet } from '../ui/Sheet';
import { VanPointsSheet } from './VanPointsSheet';
import { sourceLabel } from './VanTemplatePreview';
import { Notice, StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Field, field } from '../create/Field';
import { money } from '../money';
import type { OnHandItem, StockLocation } from './types';
import { AvailabilitySheet } from '../jobs/AvailabilitySheet';
import { TransferAllocationPicker } from './TransferAllocationPicker';

type Mode = 'count' | 'move' | 'adjust';

const REASON_LABEL: Record<string, string> = {
  count: 'Counted',
  adjust: 'Adjusted',
  transfer: 'Moved',
  receipt: 'Received',
  consume: 'Used on a job',
  return: 'Returned',
};

/**
 * One part: where it is, what happened to it, and the three things you can do about it
 * (PRD v2 §6.2). Counting is first because it's what a shop does on day one and every
 * time the books drift.
 */
export function StockSheet({
  item,
  locations,
  onClose,
}: {
  /** Derived from the page's live query, so a count taken here is visible here. */
  item: OnHandItem;
  locations: StockLocation[];
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const session = useSession();
  const canInitialize = can(session.role, 'inventory', 'manage');
  const [keys] = useState(() => createIntentKey());
  const unsent = useUnsentWrite('inventory.transfer', item.id, 'itemId');
  const failed = useFailedWriteFor('inventory.transfer', item.id, 'itemId');
  const blocked = unsent || failed;
  const [policyBusy, setPolicyBusy] = useState(false);
  const pointPending = useUnsentWrite('inventory.setReorderPoint', item.id, 'itemId');
  const vendorPending = useUnsentWrite('inventory.setItemVendor', item.id, 'itemId');
  const online = useOnline();
  const ledger = useQuery(trpc.inventory.ledger.queryOptions({ itemId: item.id }));
  const [mode, setMode] = useState<Mode>('count');
  const [locationId, setLocationId] = useState(locations[0]?.id ?? '');
  const [toLocationId, setToLocationId] = useState(locations[1]?.id ?? '');
  const [workOrderLineId, setWorkOrderLineId] = useState('');
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [qty, setQty] = useState('');
  const [note, setNote] = useState('');
  const [reasonCode, setReasonCode] = useState<AdjustmentReasonCode | ''>('');
  const [openingMode, setOpeningMode] = useState(false);
  const [unitCost, setUnitCost] = useState('');
  const [openingSkipped, setOpeningSkipped] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);

  const cellFor = (id: string) => item.cells.find((c) => c.locationId === id);
  const done = () => {
    keys.clear();
    setQty('');
    setNote('');
    setReasonCode('');
    setUnitCost('');
    void qc.invalidateQueries();
  };
  const opening = useMutation(
    trpc.inventory.openingCount.mutationOptions({
      onSuccess: (result) => {
        setWarning(result.warning);
        setOpeningSkipped(result.status === 'skipped');
        keys.clear();
        if (result.status === 'seeded') done();
        else void qc.invalidateQueries();
      },
    }),
  );
  const count = useMutation(
    trpc.inventory.count.mutationOptions({
      onSuccess: () => {
        setWarning(null);
        done();
      },
    }),
  );
  const adjust = useMutation(
    trpc.inventory.adjust.mutationOptions({
      onSuccess: () => {
        setWarning(null);
        done();
      },
    }),
  );
  const transfer = useMutation(
    trpc.inventory.transfer.mutationOptions({
      onSuccess: (r) => {
        // The move already happened — this is a warning, never a failure (§6.2).
        setWarning(
          r.wentNegative
            ? `Moved. That leaves ${formatQty(r.fromNowHundredths)} at the source — count it when you get a chance.`
            : null,
        );
        done();
      },
    }),
  );

  const busy =
    count.isPending ||
    adjust.isPending ||
    transfer.isPending ||
    opening.isPending ||
    pointPending ||
    vendorPending ||
    policyBusy;
  // Non-replayable office drafts stay mounted while unresolved. A transfer already
  // persisted offline can leave the sheet; its durable guard still refuses another tap.
  const transferPhase = mutationState(transfer);
  const preventClose =
    count.isPending ||
    adjust.isPending ||
    opening.isPending ||
    pointPending ||
    vendorPending ||
    transferPhase === 'sending' ||
    policyBusy;
  const error = opening.error ?? count.error ?? adjust.error ?? transfer.error;
  const parsed = Number(qty);
  const validQty = qty.trim() !== '' && Number.isFinite(parsed);
  const canSubmit =
    !busy &&
    (mode === 'move' ? !blocked : online) &&
    validQty &&
    Boolean(locationId) &&
    (!(mode === 'count' && openingMode) ||
      (canInitialize && online && unitCost.trim() !== '')) &&
    (mode === 'count'
      ? parsed >= 0
      : mode === 'adjust'
        ? parsed !== 0 &&
          Boolean(reasonCode) &&
          (reasonCode !== 'other' || note.trim() !== '')
        : parsed > 0 && Boolean(toLocationId) && toLocationId !== locationId);

  const submit = () => {
    if (!canSubmit) return;
    const base = { itemId: item.id, locationId, note: note.trim() || undefined };
    const withKey = <T extends object>(action: string, payload: T) => ({
      ...payload,
      ...keys.forIntent([session.orgId, session.userId, action, payload]),
    });
    if (mode === 'count' && openingMode) {
      opening.mutate(
        withKey('inventory.openingCount', {
          ...base,
          countedQty: qty,
          unitCostDollars: unitCost,
        }),
      );
    } else if (mode === 'count') {
      count.mutate(withKey('inventory.count', { ...base, countedQty: parsed }));
    } else if (mode === 'adjust') {
      if (!reasonCode) return;
      adjust.mutate(
        withKey('inventory.adjust', { ...base, qtyDelta: parsed, reasonCode }),
      );
    } else {
      transfer.mutate(
        withKey('inventory.transfer', {
          itemId: item.id,
          fromLocationId: locationId,
          toLocationId,
          qty: parsed,
          workOrderLineId: workOrderLineId || undefined,
          note: note.trim() || undefined,
        }),
      );
    }
  };

  const locationOption = (l: StockLocation) => (
    <option key={l.id} value={l.id}>
      {' '.repeat(l.depth * 3)}
      {l.name}
    </option>
  );

  return (
    <Sheet
      open
      className="[&_button]:min-h-11 [&_button]:min-w-11"
      onClose={() => {
        if (!preventClose) onClose();
      }}
      title={item.name}
      badge={
        <span className="text-sm text-muted-foreground">
          {formatQty(item.totalHundredths)} on hand
          {item.avgCostCents != null ? ` · ${money(item.avgCostCents)} avg cost` : ''}
        </span>
      }
      footer={
        <div className="space-y-2">
          {error ? (
            <Notice tone="danger" alert>
              {fieldErrorOf(error.message)}
            </Notice>
          ) : null}
          <Button className="w-full" disabled={!canSubmit} onClick={submit}>
            {transferPhase === 'queued'
              ? 'Queued — see Unsent work'
              : busy
                ? 'Saving…'
                : mode === 'count'
                  ? openingMode
                    ? 'Initialize opening stock'
                    : 'Save the count'
                  : mode === 'adjust'
                    ? 'Save the adjustment'
                    : 'Move it'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {!online && mode !== 'move' ? (
          <Notice tone="warn">Go online to save. Your inputs are kept.</Notice>
        ) : null}
        {mode === 'move' && blocked ? (
          <Notice tone="warn">
            This part has a transfer in Unsent work. Review or retry its original entry
            there before making another.
          </Notice>
        ) : null}
        {canInitialize ? (
          <Button variant="ghost" size="sm" onClick={() => setAvailabilityOpen(true)}>
            Quotes and work order availability
          </Button>
        ) : null}
        {availabilityOpen ? (
          <AvailabilitySheet
            context={{ kind: 'item', itemId: item.id, name: item.name, locationId }}
            onClose={() => setAvailabilityOpen(false)}
          />
        ) : null}
        {warning ? <Notice tone="warn">{warning}</Notice> : null}

        <section>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Where it is
          </p>
          <ul className="space-y-1 text-sm">
            {locations.map((l) => {
              const cell = cellFor(l.id);
              const own = cell?.ownHundredths ?? 0;
              const total = cell?.totalHundredths ?? 0;
              if (own === 0 && total === 0) return null;
              return (
                <li key={l.id} className="flex items-center gap-2">
                  <span
                    className="min-w-0 flex-1 truncate"
                    style={{ paddingLeft: `${l.depth * 12}px` }}
                  >
                    {l.name}
                    {/* Vans are named after their tech by default, so the owner's name is
                        only worth adding when the shop has renamed it to something else. */}
                    {l.userName && !l.name.includes(l.userName) ? (
                      <span className="text-muted-foreground"> · {l.userName}</span>
                    ) : null}
                  </span>
                  {/* A parent whose stock is all in its bins shows the rolled-up figure,
                      labelled, so nobody reads it as loose stock on the floor. */}
                  {own === 0 ? (
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {formatQty(total)} inside
                    </span>
                  ) : (
                    <span
                      className={`tabular-nums ${own < 0 ? 'font-semibold text-[var(--sw-danger-text)]' : ''}`}
                    >
                      {formatQty(own)}
                    </span>
                  )}
                </li>
              );
            })}
            {item.cells.length === 0 ? (
              <li className="text-muted-foreground">Not counted anywhere yet.</li>
            ) : null}
          </ul>
        </section>

        <fieldset
          disabled={busy}
          className="min-w-0 space-y-3 rounded-[var(--radius)] border border-border bg-surface-2 p-3"
        >
          <div className="flex gap-1">
            {(['count', 'move', 'adjust'] as Mode[]).map((m) => (
              <button
                key={m}
                type="button"
                disabled={busy}
                onClick={() => {
                  if (busy) return;
                  setMode(m);
                  setOpeningSkipped(false);
                  opening.reset();
                  setWarning(null);
                }}
                aria-pressed={mode === m}
                className={`min-h-11 flex-1 rounded-[var(--radius-sm)] px-2 py-1.5 text-sm font-medium capitalize ${
                  mode === m
                    ? 'bg-surface text-text shadow-[var(--shadow-sm)]'
                    : 'text-muted-foreground'
                }`}
              >
                {m === 'count' ? 'Count' : m === 'move' ? 'Move' : 'Adjust'}
              </button>
            ))}
          </div>

          <Field label={mode === 'move' ? 'From' : 'Location'}>
            <select
              className={`${field} min-h-11`}
              value={locationId}
              onChange={(e) => {
                if (busy) return;
                setLocationId(e.target.value);
                setWorkOrderLineId('');
                setWarning(null);
                setOpeningSkipped(false);
                opening.reset();
              }}
            >
              {locations.map(locationOption)}
            </select>
          </Field>

          {mode === 'move' ? (
            <Field label="To">
              <select
                className={`${field} min-h-11`}
                value={toLocationId}
                onChange={(e) => setToLocationId(e.target.value)}
              >
                {locations.map(locationOption)}
              </select>
            </Field>
          ) : null}
          {mode === 'move' && locationId ? (
            <TransferAllocationPicker
              itemId={item.id}
              fromLocationId={locationId}
              value={workOrderLineId}
              onChange={setWorkOrderLineId}
            />
          ) : null}

          {mode === 'count' && canInitialize ? (
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={openingMode}
                disabled={busy}
                onChange={(e) => {
                  setOpeningMode(e.target.checked);
                  setOpeningSkipped(false);
                  opening.reset();
                  setWarning(null);
                }}
                className="mt-1"
              />
              <span>One-time opening stock with unit cost</span>
            </label>
          ) : null}
          {mode === 'count' && openingMode && canInitialize ? (
            <>
              <Field label="Opening unit cost ($)">
                <input
                  className={`${field} min-h-11`}
                  inputMode="decimal"
                  value={unitCost}
                  onChange={(e) => setUnitCost(e.target.value)}
                  placeholder="Enter cost, including 0"
                  disabled={busy}
                />
              </Field>
              {!online ? (
                <Notice tone="warn">
                  Go online to initialize opening stock. Your inputs are kept.
                </Notice>
              ) : null}
              {openingSkipped ? (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setOpeningMode(false);
                    setOpeningSkipped(false);
                    opening.reset();
                  }}
                >
                  Switch to ordinary count
                </Button>
              ) : null}
            </>
          ) : null}

          <Field
            label={
              mode === 'count'
                ? 'How many are actually there'
                : mode === 'adjust'
                  ? 'Change by (use a minus for losses)'
                  : 'How many to move'
            }
          >
            <input
              className={`${field} min-h-11`}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              inputMode="decimal"
              placeholder={
                mode === 'count'
                  ? formatQty(cellFor(locationId)?.ownHundredths ?? 0)
                  : mode === 'adjust'
                    ? '-1'
                    : '4'
              }
            />
          </Field>

          {mode === 'count' && openingMode ? (
            <p className="text-xs text-muted-foreground">
              Records the opening count and its declared cost once, even if the quantity
              already matches. Operating stock is kept if this location is already
              initialized. Tracking is enabled when needed.
            </p>
          ) : mode === 'count' ? (
            <p className="text-xs text-muted-foreground">
              The books say{' '}
              <strong>{formatQty(cellFor(locationId)?.ownHundredths ?? 0)}</strong> here.
              Saving records the difference — and nothing at all if they already match.
            </p>
          ) : null}

          {mode === 'adjust' ? (
            <AdjustmentReasonFields
              reasonCode={reasonCode}
              note={note}
              onReason={(value) => {
                if (!busy) {
                  setReasonCode(value);
                  adjust.reset();
                }
              }}
              onNote={(value) => {
                if (!busy) {
                  setNote(value);
                  adjust.reset();
                }
              }}
              disabled={Boolean(busy)}
              error={adjust.error}
            />
          ) : (
            <Field label="Note (optional)">
              <input
                className={`${field} min-h-11`}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </Field>
          )}
        </fieldset>

        <RestockPolicy item={item} locations={locations} onBusyChange={setPolicyBusy} />

        <section>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            What's happened
          </p>
          {ledger.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : ledger.isError ? (
            <p className="text-sm text-muted-foreground">Couldn't load the history.</p>
          ) : ledger.data.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing recorded yet.</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {ledger.data.map((m) => {
                const delta = Math.round(Number(m.qtyDelta) * 100);
                return (
                  <li key={m.id} className="flex items-center gap-2">
                    <span
                      className={`w-14 shrink-0 text-right font-semibold tabular-nums ${
                        delta < 0
                          ? 'text-[var(--sw-danger-text)]'
                          : 'text-[var(--sw-success-text)]'
                      }`}
                    >
                      {delta > 0 ? '+' : ''}
                      {formatQty(delta)}
                    </span>
                    <span
                      className={`min-w-0 flex-1 ${m.reason === 'adjust' ? 'break-words' : 'truncate'}`}
                    >
                      <StatusBadge tone="neutral">
                        {REASON_LABEL[m.reason] ?? m.reason}
                      </StatusBadge>{' '}
                      {m.reason === 'adjust' ? (
                        <span>
                          {' '}
                          ·{' '}
                          {m.reasonCode
                            ? (ADJUSTMENT_REASON_LABELS[
                                m.reasonCode as AdjustmentReasonCode
                              ] ?? 'Not recorded')
                            : 'Not recorded'}{' '}
                        </span>
                      ) : null}
                      <span className="text-muted-foreground">{m.locationName}</span>
                      {m.note ? (
                        <span className="text-muted-foreground"> · {m.note}</span>
                      ) : null}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {new Date(m.createdAt).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </Sheet>
  );
}

/** Capture only. The shared input and server enforce the reason and conditional note. */
export function AdjustmentReasonFields({
  reasonCode,
  note,
  onReason,
  onNote,
  disabled,
  error,
}: {
  reasonCode: AdjustmentReasonCode | '';
  note: string;
  onReason: (value: AdjustmentReasonCode | '') => void;
  onNote: (value: string) => void;
  disabled: boolean;
  error?: { message: string } | null;
}) {
  const id = useId();
  const reasonError = fieldErrorAt(error?.message, ['reasonCode']);
  const noteError = fieldErrorAt(error?.message, ['note']);
  return (
    <>
      <Field label="Reason">
        <select
          className={`${field} min-h-11`}
          aria-label="Reason"
          aria-invalid={Boolean(reasonError)}
          aria-describedby={reasonError ? `${id}-reason-error` : undefined}
          value={reasonCode}
          required
          disabled={disabled}
          onChange={(e) => onReason(e.target.value as AdjustmentReasonCode | '')}
        >
          <option value="">Choose a reason</option>
          {ADJUSTMENT_REASON_CODES.map((code) => (
            <option key={code} value={code}>
              {ADJUSTMENT_REASON_LABELS[code]}
            </option>
          ))}
        </select>
      </Field>
      {reasonError ? (
        <p id={`${id}-reason-error`} className="text-xs text-[var(--sw-danger-text)]">
          {reasonError}
        </p>
      ) : null}
      {reasonCode === 'used_not_recorded' ? (
        <p className="text-xs text-muted-foreground">
          If this was used on a job, record it on the job so its cost and billing are
          included. This adjustment only changes stock.
        </p>
      ) : null}
      <Field label={reasonCode === 'other' ? 'Note (required)' : 'Note (optional)'}>
        <input
          className={`${field} min-h-11`}
          aria-label="Note"
          aria-invalid={Boolean(noteError)}
          aria-describedby={noteError ? `${id}-note-error` : undefined}
          value={note}
          required={reasonCode === 'other'}
          maxLength={500}
          disabled={disabled}
          onChange={(e) => onNote(e.target.value)}
        />
      </Field>
      {noteError ? (
        <p id={`${id}-note-error`} className="text-xs text-[var(--sw-danger-text)]">
          {noteError}
        </p>
      ) : null}
    </>
  );
}

/**
 * How this part gets restocked (§6.5): who you buy it from, and how many you want on the
 * shelf at each place. Both are optional and both live here rather than on a settings
 * screen, because the moment you notice you're short is the moment you'd set them.
 *
 * A blank threshold means "don't watch this one" — clearing has to be as easy as setting,
 * or the reorder list fills with numbers nobody meant and stops being read.
 */
export function RestockPolicy({
  item,
  locations,
  onBusyChange,
}: {
  item: OnHandItem;
  locations: StockLocation[];
  onBusyChange?: (busy: boolean) => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const session = useSession(),
    online = useOnline();
  const [vendorKeys] = useState(() => createIntentKey());
  const [pointKeys] = useState(() => createIntentKey());
  const [fill, setFill] = useState('');
  const manager = can(session.role, 'inventory', 'manage');
  const vendors = useQuery({
    ...trpc.purchasing.vendors.queryOptions(),
    enabled: manager,
  });
  const points = useQuery({
    ...trpc.inventory.reorderPoints.queryOptions(),
    enabled: !manager,
  });
  const context = useQuery(
    trpc.restocks.policyContext.queryOptions(manager ? { itemId: item.id } : skipToken),
  );
  const [vanId, setVanId] = useState<string | null>(null),
    [vanBusy, setVanBusy] = useState(false);
  const data = manager ? context.data?.points : points.data;
  const read = manager ? context : points;

  const [editing, setEditing] = useState<string | null>(null);
  const [point, setPoint] = useState('');
  const [pack, setPack] = useState('');

  const refresh = () => {
    void qc.invalidateQueries();
  };
  const setVendor = useMutation(
    trpc.inventory.setItemVendor.mutationOptions({
      onSuccess: () => {
        vendorKeys.clear();
        refresh();
      },
    }),
  );
  const setPoint_ = useMutation(
    trpc.inventory.setReorderPoint.mutationOptions({
      onSuccess: () => {
        pointKeys.clear();
        setEditing(null);
        refresh();
      },
    }),
  );

  useEffect(() => {
    onBusyChange?.(setVendor.isPending || setPoint_.isPending || vanBusy);
    return () => onBusyChange?.(false);
  }, [setVendor.isPending, setPoint_.isPending, vanBusy, onBusyChange]);

  const mine = (data ?? []).filter((p) => p.itemId === item.id);
  const pointAt = (locationId: string) => mine.find((p) => p.locationId === locationId);

  const modern =
    manager &&
    !context.error &&
    context.data?.contractVersion === 2 &&
    mine.every(
      (p) =>
        typeof p.fillToHundredths === 'number' &&
        ['template', 'local', 'van_only', 'by_hand'].includes(p.source),
    );
  const writeKey = (action: string, payload: unknown) =>
    (action === 'inventory.setItemVendor' ? vendorKeys : pointKeys).forIntent([
      session.orgId,
      session.userId,
      action,
      payload,
    ]);
  const open = (locationId: string) => {
    if (!modern || !online || !manager) return;
    const assignment = context.data?.locations.find((l) => l.locationId === locationId);
    if (!assignment) return;
    if (assignment.templateId !== null) {
      setVanId(locationId);
      return;
    }
    const existing = pointAt(locationId);
    setPoint_.reset();
    setPoint(existing ? formatQty(existing.reorderPointHundredths) : '');
    setFill(existing ? formatQty(existing.fillToHundredths) : '');
    setPack(
      existing?.reorderQtyHundredths ? formatQty(existing.reorderQtyHundredths) : '',
    );
    setEditing(locationId);
  };

  const state = shownState(read, read.data);
  if (state === 'loading')
    return (
      <p className="text-sm text-muted-foreground" role="status">
        Loading restocking settings…
      </p>
    );
  if (state === 'offline')
    return <OfflineState icon={IconInventory} what="restocking settings" />;
  if (state === 'error')
    return (
      <ErrorState
        icon={IconInventory}
        what="restocking settings"
        onRetry={() => void (manager ? context : points).refetch()}
      />
    );
  return (
    <section className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Restocking
      </p>

      <StaleNotice
        show={refreshFailed(read, read.data)}
        onRetry={() => void (manager ? context : points).refetch()}
      />
      {setVendor.error ? (
        <Notice tone="danger" alert>
          {setVendor.error.message}
        </Notice>
      ) : null}
      {setPoint_.error ? (
        <Notice tone="danger" alert>
          {fieldErrorOf(setPoint_.error.message)}
        </Notice>
      ) : null}

      {!online ? (
        <Notice tone="warn">Go online to change restocking settings.</Notice>
      ) : null}
      {manager && !modern ? (
        <Notice tone="warn">
          Refresh saved settings before editing this older response.{' '}
          <Button
            variant="ghost"
            className="min-h-11"
            onClick={() => void context.refetch()}
          >
            Refresh
          </Button>
        </Notice>
      ) : null}
      <Field label="Normally bought from">
        <select
          className={`${field} min-h-11`}
          value={item.preferredVendorId ?? ''}
          disabled={!manager || setVendor.isPending || !online}
          onChange={(e) =>
            setVendor.mutate({
              itemId: item.id,
              vendorId: e.target.value || null,
              ...writeKey('inventory.setItemVendor', {
                itemId: item.id,
                vendorId: e.target.value || null,
              }),
            })
          }
        >
          <option value="">Nobody in particular</option>
          {(vendors.data ?? []).map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
      </Field>
      {item.preferredVendorId === null && mine.length > 0 ? (
        <p className="text-xs text-[var(--sw-warn-text)]">
          Without a vendor this can't go on a suggested order — it'll show in the reorder
          list but can't be drafted.
        </p>
      ) : null}

      <ul className="space-y-1 text-sm">
        {locations.map((l) => {
          const existing = pointAt(l.id);
          const assignment = context.data?.locations.find((c) => c.locationId === l.id);
          if (manager && assignment?.templateId)
            return (
              <li key={l.id} className="space-y-1 border-b border-border py-2">
                <p>
                  {l.name} · {assignment.templateName ?? 'Unavailable template'}
                </p>
                {existing ? (
                  <p className="text-xs">
                    {sourceLabel(existing.source)} · keep{' '}
                    {formatQty(existing.reorderPointHundredths)} · fill{' '}
                    {formatQty(existing.fillToHundredths)}
                    {existing.reorderQtyHundredths !== null
                      ? ` · packs of ${formatQty(existing.reorderQtyHundredths)}`
                      : ''}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">Not watched</p>
                )}
                {assignment.templateIssue ? <p>{assignment.templateIssue}</p> : null}
                <Button
                  variant="ghost"
                  className="min-h-11"
                  disabled={
                    !modern || !online || setPoint_.isPending || !assignment.active
                  }
                  onClick={() => setVanId(l.id)}
                >
                  Edit van policy
                </Button>
              </li>
            );
          if (editing === l.id) {
            return (
              <li key={l.id} className="rounded-[var(--radius)] bg-surface-2 p-2">
                <p className="mb-2 text-xs font-medium">{l.name}</p>
                <fieldset
                  disabled={!manager || !modern || !online || setPoint_.isPending}
                  className="min-w-0"
                >
                  <RestockPolicyFields
                    point={point}
                    fill={fill}
                    pack={pack}
                    onPoint={setPoint}
                    onFill={setFill}
                    onPack={setPack}
                    error={setPoint_.error}
                    disabled={!manager || !modern || setPoint_.isPending || !online}
                  />
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button variant="ghost" onClick={() => setEditing(null)}>
                      Cancel
                    </Button>
                    {existing ? (
                      <Button
                        variant="ghost"
                        disabled={!manager || !modern || setPoint_.isPending || !online}
                        onClick={() => {
                          if (!manager || !modern || !online) return;
                          setPoint_.mutate({
                            itemId: item.id,
                            locationId: l.id,
                            reorderPoint: null,
                            ...writeKey('inventory.setReorderPoint', {
                              itemId: item.id,
                              locationId: l.id,
                              reorderPoint: null,
                            }),
                          });
                        }}
                      >
                        Stop watching
                      </Button>
                    ) : null}
                    <Button
                      className="ml-auto"
                      disabled={
                        setPoint_.isPending ||
                        !online ||
                        !modern ||
                        !fill.trim() ||
                        !!numberTextIssue(point, 'Keep') ||
                        !!numberTextIssue(fill, 'Fill') ||
                        !!numberTextIssue(pack, 'Purchase pack') ||
                        !(Number(point) >= 0 && point.trim())
                      }
                      onClick={() =>
                        setPoint_.mutate({
                          itemId: item.id,
                          locationId: l.id,
                          reorderPoint: Number(point),
                          fillTo: Number(fill),
                          reorderQty: pack.trim() ? Number(pack) : null,
                          ...writeKey('inventory.setReorderPoint', {
                            itemId: item.id,
                            locationId: l.id,
                            reorderPoint: Number(point),
                            fillTo: Number(fill),
                            reorderQty: pack.trim() ? Number(pack) : null,
                          }),
                        })
                      }
                    >
                      Save
                    </Button>
                  </div>
                </fieldset>
              </li>
            );
          }
          return (
            <li key={l.id} className="flex items-center gap-2">
              <span
                className="min-w-0 flex-1 truncate"
                style={{ paddingLeft: `${l.depth * 12}px` }}
              >
                {l.name}
              </span>
              <button
                type="button"
                className="min-h-11 shrink-0 text-xs underline decoration-dotted hover:text-text"
                disabled={
                  !manager || !modern || !online || setPoint_.isPending || !assignment
                }
                onClick={() => open(l.id)}
              >
                {existing ? (
                  <span className="text-muted-foreground">
                    keep {formatQty(existing.reorderPointHundredths)}
                    {typeof existing.fillToHundredths === 'number'
                      ? ` · fill ${formatQty(existing.fillToHundredths)} · ${sourceLabel(existing.source)}`
                      : ''}
                    {existing.reorderQtyHundredths
                      ? ` · packs of ${formatQty(existing.reorderQtyHundredths)}`
                      : ''}
                  </span>
                ) : (
                  <span className="text-muted-foreground">not watched</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      {vanId ? (
        <VanPointsSheet
          key={`${session.orgId}:${session.userId}:${vanId}`}
          vanId={vanId}
          onClose={() => setVanId(null)}
          onBusyChange={setVanBusy}
        />
      ) : null}
    </section>
  );
}

/** Controlled capture fields; server issues keep their exact path and authored message. */
export function RestockPolicyFields({
  point,
  fill,
  pack,
  onPoint,
  onFill,
  onPack,
  error,
  disabled = false,
}: {
  point: string;
  fill: string;
  pack: string;
  onPoint: (value: string) => void;
  onFill: (value: string) => void;
  onPack: (value: string) => void;
  error?: { message: string } | null;
  disabled?: boolean;
}) {
  const id = useId();
  const keepIssue =
    numberTextIssue(point, 'Keep') ?? fieldErrorAt(error?.message, ['reorderPoint']);
  const fillIssue =
    numberTextIssue(fill, 'Fill') ?? fieldErrorAt(error?.message, ['fillTo']);
  const packIssue =
    numberTextIssue(pack, 'Purchase pack') ??
    fieldErrorAt(error?.message, ['reorderQty']);
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Keep at least">
          <input
            className={`${field} min-h-11`}
            required
            aria-label="Keep at least"
            value={point}
            onChange={(e) => onPoint(e.target.value)}
            inputMode="decimal"
            placeholder="4"
            autoFocus
            disabled={disabled}
            aria-invalid={keepIssue ? true : undefined}
            aria-describedby={keepIssue ? `${id}-keep` : undefined}
          />
          {keepIssue ? (
            <p
              id={`${id}-keep`}
              role="alert"
              className="mt-1 text-xs text-[var(--sw-danger-text)]"
            >
              {keepIssue}
            </p>
          ) : null}
        </Field>
        <Field label="Fill to">
          <input
            className={`${field} min-h-11`}
            required
            aria-label="Fill to"
            value={fill}
            onChange={(e) => onFill(e.target.value)}
            inputMode="decimal"
            placeholder="Enter Fill"
            disabled={disabled}
            aria-invalid={fillIssue ? true : undefined}
            aria-describedby={fillIssue ? `${id}-fill` : undefined}
          />
          {fillIssue ? (
            <p
              id={`${id}-fill`}
              role="alert"
              className="mt-1 text-xs text-[var(--sw-danger-text)]"
            >
              {fillIssue}
            </p>
          ) : null}
        </Field>
      </div>
      <div className="mt-2">
        <Field label="Buy in packs of">
          <input
            className={`${field} min-h-11`}
            aria-label="Buy in packs of"
            value={pack}
            onChange={(e) => onPack(e.target.value)}
            inputMode="decimal"
            placeholder="any"
            disabled={disabled}
            aria-invalid={packIssue ? true : undefined}
            aria-describedby={packIssue ? `${id}-pack` : undefined}
          />
          {packIssue ? (
            <p
              id={`${id}-pack`}
              role="alert"
              className="mt-1 text-xs text-[var(--sw-danger-text)]"
            >
              {packIssue}
            </p>
          ) : null}
        </Field>
      </div>
    </>
  );
}
